import "@/styles/globals.css";
import type { AppProps } from "next/app";
import { SessionProvider } from "next-auth/react";
import { DarkThemeContextProvider } from "@/contexts/DarkTheme";
import { LoadingContextProvider } from "@/contexts/Loading";
import { TickerContextProvider } from "@/contexts/TickerContext";
import { VfundsContextProvider } from "@/contexts/VfundsContext";
import { PortfolioContextProvider } from "@/contexts/PortfolioContext";
import { ModalContextProvider } from "@/contexts/ModalContext";
import { RecoilRoot } from "recoil";
import TopProgressBar from "@/components/TopProgressBar";
import { appWithTranslation } from "next-i18next";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Hotjar from "@hotjar/browser";
import Head from "next/head";
import GoogleAnalytics from "@/components/GA";
import { ConnectFormContextProvider } from "@/contexts/ConnectFormContext";
import { SubscriptionContextProvider } from "@/contexts/SubscriptionContext";
import { AIAssistantContextProvider } from "@/contexts/AIAssistantContext";
import { BacktestTourContextProvider } from "@/contexts/BacktestTourContext";
import { SidebarContextProvider } from "@/contexts/SidebarContext";
import { TechnicalRequestContextProvider } from "@/contexts/TechnicalRequestContext";
import { PatternUniverseContextProvider } from "@/contexts/PatternUniverse";
import { QuickStrategyContextProvider } from "@/contexts/QuickStrategyContext";
import { PrebuiltScannerDataProvider } from "@/contexts/PrebuiltScannerDataContext";
import { UpgradePlanModalContextProvider } from "@/contexts/UpgradePlanModalContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { UserProvider } from "@/contexts/userContext";
import DialogueWrapper from "@/components/hoc/DialogueWrapper";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { NotificationSocketProvider } from "@/contexts/NotificationSocketContext";

if (typeof window !== "undefined") {
  // checks that we are client-side
  const siteId = 3751159;
  const hotjarVersion = 6;

  Hotjar.init(siteId, hotjarVersion);
}
const queryClient = new QueryClient();

const App = ({ Component, pageProps: { session, ...pageProps } }: AppProps) => {
  return (
    <>
      <Head>
        <GoogleAnalytics GA_TRACKING_ID={"G-NRPKERR6KT"} />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1"
          key="viewport"
        />
        <link rel="manifest" href="/manifest.json" />
        <meta
          name="description"
          content="Making it easy for anyone to turn an idea into a fully automated trading strategy without any coding whatsoever."
        />

        {/* Open Graph */}
        <meta property="og:url" content="https://trylevel2.com" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Nextrades: The first fully visual no-code systematic trading strategy creation platform built for active traders."
        />
        <meta
          property="og:description"
          content="Making it easy for anyone to turn an idea into a fully automated trading strategy without any coding whatsoever."
        />
        <meta
          property="og:image"
          content="https://hub2.trylevel2.com/static_images/l2_og.jpg"
        />
        <meta
          property="og:image:secure_url"
          content="https://hub2.trylevel2.com/static_images/l2_og.jpg"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="trylevel2.com" />
        <meta property="twitter:url" content="https://trylevel2.com" />
        <meta
          name="twitter:title"
          content="Nextrades: The first fully visual no-code systematic trading strategy creation platform built for active traders."
        />
        <meta
          name="twitter:description"
          content="Making it easy for anyone to turn an idea into a fully automated trading strategy without any coding whatsoever."
        />
        <meta
          name="twitter:image"
          content="https://hub2.trylevel2.com/static_images/l2_og.jpg"
        />
      </Head>

      <SessionProvider session={session}>
        <Toaster />
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <NotificationSocketProvider>
              <DarkThemeContextProvider>
                <BacktestTourContextProvider>
                  <SidebarContextProvider>
                    <TechnicalRequestContextProvider>
                      <AIAssistantContextProvider>
                        <ConnectFormContextProvider>
                          <SubscriptionContextProvider>
                            <LoadingContextProvider>
                              <TickerContextProvider>
                                <VfundsContextProvider>
                                  <PortfolioContextProvider>
                                    <ModalContextProvider>
                                      <PatternUniverseContextProvider>
                                        <RecoilRoot>
                                          <TooltipProvider>
                                            <QuickStrategyContextProvider>
                                              <PrebuiltScannerDataProvider>
                                                <UpgradePlanModalContextProvider>
                                                  <TopProgressBar />
                                                  <DialogueWrapper>
                                                    <Component {...pageProps} />
                                                  </DialogueWrapper>
                                                </UpgradePlanModalContextProvider>
                                              </PrebuiltScannerDataProvider>
                                            </QuickStrategyContextProvider>
                                          </TooltipProvider>
                                        </RecoilRoot>
                                      </PatternUniverseContextProvider>
                                    </ModalContextProvider>
                                  </PortfolioContextProvider>
                                </VfundsContextProvider>
                              </TickerContextProvider>
                            </LoadingContextProvider>
                          </SubscriptionContextProvider>
                        </ConnectFormContextProvider>
                      </AIAssistantContextProvider>
                    </TechnicalRequestContextProvider>
                  </SidebarContextProvider>
                </BacktestTourContextProvider>
              </DarkThemeContextProvider>
            </NotificationSocketProvider>
          </UserProvider>
        </QueryClientProvider>
      </SessionProvider>
    </>
  );
};
export default appWithTranslation(App);
export const getServerSideProps = async ({ params, locale }: any) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || "en", [
        "marketplace",
        "layout",
        "slug",
        "build",
        "market-screener",
        "layout",
        "common",
        "test",
        "trade",
        "walk-through",
        "alert-strategy",
        "add-ons-form",
      ])),
    },
  };
};
